import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Download, Loader2 } from 'lucide-react';
import type { PriceListColumnKey } from '@/lib/price-list-doc';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';

type Granularity = 'lot' | 'product';
type Pricing = 'sell' | 'cost';
type Format = 'pdf' | 'xlsx';
type CurrencyChoice = 'native' | 'TRY' | 'EUR' | 'USD' | 'GBP' | 'all';
type KdvChoice = 'incl' | 'excl';
type PriceType = 'retail' | 'vendor';

export interface PriceListExportOptions {
  mode: Granularity;
  includeCost: boolean;
  format: Format;
  // Target currencies (converted with the current/today FX). Empty = native
  // (each lot in its own currency, no conversion).
  currencies: string[];
  // Whether the sell price includes KDV. Default inclusive, matching the
  // product detail page and the price used on quotes/orders.
  withKdv: boolean;
  // Which markup to price from: 'retail' (default) or 'vendor' (dealer).
  priceType: PriceType;
  // Embed inline product thumbnails (PDF only).
  includeImages: boolean;
  // Logical columns to render (the dialog's checkbox selection). The product
  // name and sell price are mandatory and not listed here.
  columns: PriceListColumnKey[];
}

/**
 * User-toggleable columns, shown contextually: `modes` limits a column to a
 * granularity, `vendorOnly` to the dealer list (the companion SRP column),
 * `nativeOnly` to the product-currency mode (forced-currency exports have no
 * line-value/summary rows, so the checkbox would be a dead control there).
 */
interface ColumnDef {
  key: PriceListColumnKey;
  modes: Granularity[];
  vendorOnly?: boolean;
  nativeOnly?: boolean;
  label: (t: TFunction) => string;
}

const COLUMN_DEFS: ColumnDef[] = [
  { key: 'code', modes: ['lot', 'product'], label: (t) => t('products.exportColCode', 'Kod') },
  { key: 'brand', modes: ['lot', 'product'], label: (t) => t('products.exportColBrand', 'Marka') },
  { key: 'batch', modes: ['lot'], label: (t) => t('products.exportColBatch', 'Parti No') },
  { key: 'received', modes: ['lot'], label: (t) => t('products.exportColReceived', 'Giriş Tarihi') },
  { key: 'qty', modes: ['lot'], label: (t) => t('products.exportColQty', 'Mevcut Miktar') },
  { key: 'totalQty', modes: ['product'], label: (t) => t('products.exportColTotalQty', 'Toplam Mevcut') },
  { key: 'lotQty', modes: ['product'], label: (t) => t('products.exportColLotQty', 'Bu Fiyatta Miktar') },
  {
    key: 'retail',
    modes: ['lot', 'product'],
    vendorOnly: true,
    label: (t) => t('products.exportColRetail', 'Perakende (SRP)'),
  },
  {
    key: 'lineValue',
    modes: ['lot'],
    nativeOnly: true,
    label: (t) => t('products.exportColLineValue', 'Satır Değeri ve toplamlar'),
  },
];

function currenciesFor(choice: CurrencyChoice): string[] {
  if (choice === 'native') return [];
  if (choice === 'all') return ['EUR', 'USD', 'GBP', 'TRY'];
  return [choice];
}

interface PriceListExportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  exporting: boolean;
  onExport: (options: PriceListExportOptions) => void;
}

interface ChoiceRowProps {
  id: string;
  value: string;
  label: string;
}

function ChoiceRow({ id, value, label }: ChoiceRowProps) {
  return (
    <div className="flex items-center gap-2">
      <RadioGroupItem id={id} value={value} />
      <Label htmlFor={id} className="cursor-pointer text-sm font-normal">
        {label}
      </Label>
    </div>
  );
}

export function PriceListExportDialog({
  open,
  onOpenChange,
  exporting,
  onExport,
}: PriceListExportDialogProps) {
  const { t } = useTranslation('trading');

  const [priceType, setPriceType] = useState<PriceType>('retail');
  const [mode, setMode] = useState<Granularity>('lot');
  const [pricing, setPricing] = useState<Pricing>('sell');
  const [format, setFormat] = useState<Format>('pdf');
  const [currency, setCurrency] = useState<CurrencyChoice>('native');
  const [kdv, setKdv] = useState<KdvChoice>('incl');
  const [includeImages, setIncludeImages] = useState(false);
  // Deselected column keys — storing the negative keeps every column checked
  // by default and preserves choices when switching between modes.
  const [hiddenColumns, setHiddenColumns] = useState<ReadonlySet<PriceListColumnKey>>(
    () => new Set()
  );

  // Inline thumbnails are a PDF-only feature.
  const imagesAvailable = format === 'pdf';

  // Columns applicable to the current mode/list-type/currency selection.
  const applicableColumns = COLUMN_DEFS.filter(
    (c) =>
      c.modes.includes(mode) &&
      (!c.vendorOnly || priceType === 'vendor') &&
      (!c.nativeOnly || currency === 'native')
  );

  const toggleColumn = (key: PriceListColumnKey, checked: boolean) => {
    setHiddenColumns((prev) => {
      const next = new Set(prev);
      if (checked) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleExport = () => {
    onExport({
      mode,
      includeCost: pricing === 'cost',
      format,
      currencies: currenciesFor(currency),
      withKdv: kdv === 'incl',
      priceType,
      includeImages: includeImages && imagesAvailable,
      columns: applicableColumns.filter((c) => !hiddenColumns.has(c.key)).map((c) => c.key),
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* Taller than small viewports (many option groups) — must scroll. */}
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('products.exportTitle', 'Fiyat Listesini Dışa Aktar')}</DialogTitle>
          <DialogDescription>
            {t('products.exportDescription', 'Dışa aktarma seçeneklerini belirleyin.')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-1">
          {/* Price type — retail vs vendor (dealer); each has its own markup */}
          <div className="space-y-2">
            <Label className="text-muted-foreground text-xs font-medium uppercase tracking-wide">
              {t('products.exportPriceType', 'Liste türü')}
            </Label>
            <RadioGroup value={priceType} onValueChange={(v) => setPriceType(v as PriceType)}>
              <ChoiceRow
                id="export-type-retail"
                value="retail"
                label={t('products.exportPriceTypeRetail', 'Perakende (satış)')}
              />
              <ChoiceRow
                id="export-type-vendor"
                value="vendor"
                label={t('products.exportPriceTypeVendor', 'Bayi (satıcı)')}
              />
            </RadioGroup>
          </div>

          {/* Granularity */}
          <div className="space-y-2">
            <Label className="text-muted-foreground text-xs font-medium uppercase tracking-wide">
              {t('products.exportGranularity', 'Ayrıntı düzeyi')}
            </Label>
            <RadioGroup value={mode} onValueChange={(v) => setMode(v as Granularity)}>
              <ChoiceRow
                id="export-mode-lot"
                value="lot"
                label={t('products.exportModeLot', 'Parti bazlı')}
              />
              <ChoiceRow
                id="export-mode-product"
                value="product"
                label={t('products.exportModeProduct', 'Ürün bazlı')}
              />
            </RadioGroup>
          </div>

          {/* Columns — full control over what lands on the document. Name and
              sell price are the list's reason to exist, so they render as
              checked-and-locked; the rest follow the checkbox selection. */}
          <div className="space-y-2">
            <Label className="text-muted-foreground text-xs font-medium uppercase tracking-wide">
              {t('products.exportColumns', 'Sütunlar')}
            </Label>
            <div className="grid grid-cols-2 gap-x-4 gap-y-2">
              <div className="flex items-center gap-2">
                <Checkbox id="export-col-name" checked disabled />
                <Label htmlFor="export-col-name" className="text-sm font-normal opacity-50">
                  {t('products.exportColProduct', 'Ürün')}
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox id="export-col-sell" checked disabled />
                <Label htmlFor="export-col-sell" className="text-sm font-normal opacity-50">
                  {t('products.exportColSell', 'Satış Fiyatı')}
                </Label>
              </div>
              {applicableColumns.map((col) => (
                <div key={col.key} className="flex items-center gap-2">
                  <Checkbox
                    id={`export-col-${col.key}`}
                    checked={!hiddenColumns.has(col.key)}
                    onCheckedChange={(c) => toggleColumn(col.key, c === true)}
                  />
                  <Label
                    htmlFor={`export-col-${col.key}`}
                    className="cursor-pointer text-sm font-normal"
                  >
                    {col.label(t)}
                  </Label>
                </div>
              ))}
            </div>
          </div>

          {/* Pricing */}
          <div className="space-y-2">
            <Label className="text-muted-foreground text-xs font-medium uppercase tracking-wide">
              {t('products.exportPricing', 'Fiyatlandırma')}
            </Label>
            <RadioGroup value={pricing} onValueChange={(v) => setPricing(v as Pricing)}>
              <ChoiceRow
                id="export-pricing-sell"
                value="sell"
                label={t('products.exportPricingSell', 'Sadece satış fiyatı')}
              />
              <ChoiceRow
                id="export-pricing-cost"
                value="cost"
                label={t('products.exportPricingCost', 'Maliyet dahil')}
              />
            </RadioGroup>
          </div>

          {/* KDV — whether the sell price includes VAT (default: included) */}
          <div className="space-y-2">
            <Label className="text-muted-foreground text-xs font-medium uppercase tracking-wide">
              {t('products.exportKdv', 'KDV')}
            </Label>
            <RadioGroup value={kdv} onValueChange={(v) => setKdv(v as KdvChoice)}>
              <ChoiceRow
                id="export-kdv-incl"
                value="incl"
                label={t('products.exportKdvIncl', 'KDV dahil')}
              />
              <ChoiceRow
                id="export-kdv-excl"
                value="excl"
                label={t('products.exportKdvExcl', 'KDV hariç')}
              />
            </RadioGroup>
          </div>

          {/* Currency — native keeps each price in its product's own currency
              (frozen purchase-date cost basis, no conversion); the forced
              choices express that finished price in the selected currencies
              at today's live FX. */}
          <div className="space-y-2">
            <Label className="text-muted-foreground text-xs font-medium uppercase tracking-wide">
              {t('products.exportCurrency', 'Para birimi')}
            </Label>
            <RadioGroup value={currency} onValueChange={(v) => setCurrency(v as CurrencyChoice)}>
              <ChoiceRow
                id="export-cur-native"
                value="native"
                label={t('products.exportCurrencyNative', 'Ürün para birimi (alım kuru — sabit)')}
              />
              <ChoiceRow id="export-cur-try" value="TRY" label="TL" />
              <ChoiceRow id="export-cur-eur" value="EUR" label="EUR" />
              <ChoiceRow id="export-cur-usd" value="USD" label="USD" />
              <ChoiceRow id="export-cur-gbp" value="GBP" label="STG" />
              <ChoiceRow
                id="export-cur-all"
                value="all"
                label={t('products.exportCurrencyAll', 'Tümü (EUR/USD/STG/TL)')}
              />
            </RadioGroup>
          </div>

          {/* Format */}
          <div className="space-y-2">
            <Label className="text-muted-foreground text-xs font-medium uppercase tracking-wide">
              {t('products.exportFormat', 'Biçim')}
            </Label>
            <RadioGroup value={format} onValueChange={(v) => setFormat(v as Format)}>
              <ChoiceRow
                id="export-format-pdf"
                value="pdf"
                label={t('products.exportFormatPdf', 'PDF')}
              />
              <ChoiceRow
                id="export-format-xlsx"
                value="xlsx"
                label={t('products.exportFormatExcel', 'Excel')}
              />
            </RadioGroup>
          </div>

          {/* Images — inline thumbnails (PDF only) */}
          <div className="space-y-2">
            <Label className="text-muted-foreground text-xs font-medium uppercase tracking-wide">
              {t('products.exportImages', 'Görseller')}
            </Label>
            <div className="flex items-center gap-2">
              <Checkbox
                id="export-include-images"
                checked={includeImages && imagesAvailable}
                disabled={!imagesAvailable}
                onCheckedChange={(c) => setIncludeImages(c)}
              />
              <Label
                htmlFor="export-include-images"
                className={`cursor-pointer text-sm font-normal ${imagesAvailable ? '' : 'opacity-50'}`}
              >
                {t('products.exportIncludeImages', 'Ürün görsellerini ekle (küçük resim)')}
              </Label>
            </div>
            {!imagesAvailable && (
              <p className="text-muted-foreground text-xs">
                {t('products.exportImagesPdfOnly', 'Görseller yalnızca PDF biçiminde eklenebilir.')}
              </p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={exporting}>
            {t('products.cancel', 'İptal')}
          </Button>
          <Button onClick={handleExport} disabled={exporting}>
            {exporting ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Download className="mr-2 h-4 w-4" />
            )}
            {t('products.exportAction', 'Dışa Aktar')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
